import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../../http';

const STATUS_META = {
  active: { bg: '#c6f6d5', color: '#22543d', label: 'Live' },
  completed: { bg: '#ebf8ff', color: '#2c5282', label: 'Completed' },
  archived: { bg: '#edf2f7', color: '#4a5568', label: 'Archived' },
  setup: { bg: '#fefcbf', color: '#744210', label: 'Setup' },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.setup;
  return (
    <span
      style={{
        padding: '0.2rem 0.6rem',
        borderRadius: '9999px',
        fontWeight: 600,
        fontSize: '0.75rem',
        background: m.bg,
        color: m.color,
      }}
    >
      {m.label}
    </span>
  );
}

// Sort events: active first, then completed, then archived, then setup; within each group by date desc
const STATUS_ORDER = { active: 0, completed: 1, archived: 2, setup: 3 };

export default function ReportsHub() {
  const [events, setEvents] = useState([]);
  const [aggregate, setAggregate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [evRes, aggRes] = await Promise.all([
          axios.get('/api/test-events?include_archived=true'),
          axios.get('/api/reports/aggregate?status=all').catch(() => ({ data: null })),
        ]);
        const sorted = (Array.isArray(evRes.data) ? evRes.data : []).sort((a, b) => {
          const os = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
          if (os !== 0) return os;
          return new Date(b.event_date) - new Date(a.event_date);
        });
        setEvents(sorted);
        setAggregate(aggRes.data);
      } catch (err) {
        console.error('Failed to load reports hub', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const handleDownload = async (eventId, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      setDownloading(eventId);
      const res = await axios.get(`/api/test-events/${eventId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = res.headers['content-disposition'];
      let fileName = `Results-${eventId}.xlsx`;
      if (cd) {
        const m = cd.match(/filename="?([^"]+)"?/);
        if (m) fileName = m[1];
      }
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download.');
    } finally {
      setDownloading(null);
    }
  };

  const completedEventCount = events.filter(
    (e) => e.status === 'completed' || e.status === 'archived'
  ).length;

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Reports</h1>
          <p style={{ color: '#718096', margin: '0.25rem 0 0' }}>
            View detailed results for any test event.
          </p>
        </div>
        <Link to="/admin" className="btn btn-secondary">
          ← Admin Home
        </Link>
      </div>

      {/* Aggregate stats bar — only show if 2+ completed events */}
      {aggregate && completedEventCount >= 2 && (
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap',
            marginBottom: '2rem',
            background: '#f7fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '1rem 1.5rem',
            alignItems: 'center',
          }}
        >
          <span style={{ fontWeight: 600, color: '#4a5568', marginRight: '0.5rem' }}>
            Across all completed events:
          </span>
          <span style={{ color: '#2d3748' }}>
            <strong>{aggregate.aggregate.total_companies}</strong> companies
          </span>
          <span style={{ color: '#718096' }}>·</span>
          <span style={{ color: '#2d3748' }}>
            <strong>{aggregate.aggregate.total_sessions}</strong> station sessions
          </span>
          {aggregate.aggregate.overall_pass_rate !== null && (
            <>
              <span style={{ color: '#718096' }}>·</span>
              <span style={{ color: '#2f855a', fontWeight: 700 }}>
                {aggregate.aggregate.overall_pass_rate}% overall pass rate
              </span>
            </>
          )}
          <Link
            to="/admin/reports/aggregate"
            style={{ marginLeft: 'auto', fontSize: '0.875rem', color: '#4a90d9' }}
          >
            View aggregate breakdown →
          </Link>
        </div>
      )}

      {/* Events table */}
      {loading ? (
        <div style={{ padding: '2rem', color: '#718096' }}>Loading events...</div>
      ) : events.length === 0 ? (
        <div style={{ padding: '2rem', color: '#a0aec0' }}>No test events found.</div>
      ) : (
        <div
          style={{
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f7fafc', borderBottom: '2px solid #edf2f7' }}>
              <tr>
                <th style={thStyle}>Event</th>
                <th style={thStyle}>Date</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Stations</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id} style={{ borderBottom: '1px solid #edf2f7' }}>
                  <td style={tdStyle}>
                    <strong>{event.name}</strong>
                    {event.location && (
                      <div style={{ fontSize: '0.8rem', color: '#718096' }}>{event.location}</div>
                    )}
                  </td>
                  <td style={{ ...tdStyle, color: '#4a5568' }}>
                    {new Date(event.event_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <StatusBadge status={event.status} />
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: '#718096' }}>
                    {event.station_count ?? '—'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Link
                        to={`/admin/reports/${event.id}`}
                        className="btn btn-sm btn-primary"
                      >
                        View Report
                      </Link>
                      {(event.status === 'completed' || event.status === 'archived') && (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={(e) => handleDownload(event.id, e)}
                          disabled={downloading === event.id}
                        >
                          {downloading === event.id ? '⏳' : '📥 Excel'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: '0.75rem 1rem',
  textAlign: 'left',
  fontWeight: 600,
  color: '#4a5568',
  fontSize: '0.875rem',
};

const tdStyle = {
  padding: '0.75rem 1rem',
  verticalAlign: 'middle',
};
